import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ============================================================================
// note-client-mod -- 笔记公开详情页
//
// 路径：/notes/:id
// 展示公开笔记的 Markdown 渲染内容。支持 SSR 预取。
// ============================================================================
import * as React from 'react';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { radius, spacing, typography, getVisibilityColors } from '../styles/theme.js';
import { KEYFRAMES_CSS } from '../styles/animations.js';
import { getNoteApiClient } from '../note-api-client.js';
import { useRoute } from '@hvsin/mod-route';
import { PublicIcon } from '../components/icons.js';
/* -------------------------------------------------------------------------- */
/* 简易 Markdown 渲染器（不引入外部依赖）                                       */
/* -------------------------------------------------------------------------- */
/** 将单行纯文本渲染成带行内 Markdown 的 JSX */
function renderInline(line, keyPrefix) {
    const nodes = [];
    const regex = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
    let lastIndex = 0;
    let match;
    let i = 0;
    while ((match = regex.exec(line)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(line.slice(lastIndex, match.index));
        }
        if (match[2] !== undefined) {
            nodes.push(_jsx("strong", { children: match[2] }, `${keyPrefix}-b${i}`));
        }
        else if (match[3] !== undefined) {
            nodes.push(_jsx("code", { style: { backgroundColor: 'rgba(0,0,0,0.08)', padding: '1px 5px', borderRadius: '4px', fontSize: '0.92em' }, children: match[3] }, `${keyPrefix}-c${i}`));
        }
        else if (match[4] !== undefined) {
            nodes.push(_jsx("a", { href: match[5], target: "_blank", rel: "noreferrer", style: { color: 'inherit' }, children: match[4] }, `${keyPrefix}-a${i}`));
        }
        lastIndex = match.index + match[0].length;
        i++;
    }
    if (lastIndex < line.length) {
        nodes.push(line.slice(lastIndex));
    }
    return nodes.length === 0 ? [line] : nodes;
}
/** 将 Markdown 文本渲染为 JSX 块 */
function renderMarkdown(content) {
    const lines = content.split('\n');
    const blocks = [];
    let listStack = [];
    let index = 0;
    const flushLists = () => {
        while (listStack.length > 0) {
            const list = listStack.pop();
            blocks.push(list.ordered ? (_jsx("ol", { style: { paddingLeft: '24px', margin: '4px 0' }, children: list.items }, `ol-${index++}`)) : (_jsx("ul", { style: { paddingLeft: '24px', margin: '4px 0' }, children: list.items }, `ul-${index++}`)));
        }
    };
    for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
        const raw = lines[lineIdx];
        const line = raw.replace(/\r$/, '');
        // 代码块
        if (line.startsWith('```')) {
            flushLists();
            const codeLines = [];
            lineIdx++;
            while (lineIdx < lines.length && !lines[lineIdx].startsWith('```')) {
                codeLines.push(lines[lineIdx]);
                lineIdx++;
            }
            // 跳过结尾的 ```（若存在），或停在末尾
            blocks.push(_jsx("pre", { style: { backgroundColor: 'rgba(0,0,0,0.06)', padding: '12px 16px', borderRadius: radius.md, overflowX: 'auto', fontSize: '13px', lineHeight: 1.6 }, children: codeLines.join('\n') || ' ' }, `pre-${index++}`));
            continue;
        }
        // 标题
        const heading = /^(#{1,6})\s+(.*)$/.exec(line);
        if (heading) {
            flushLists();
            const level = heading[1].length;
            const style = {
                fontSize: ['', '1.6em', '1.4em', '1.25em', '1.15em', '1.05em', '1em'][level],
                fontWeight: typography.fontWeight.semibold,
                margin: '14px 0 8px 0',
                lineHeight: 1.35,
            };
            blocks.push(_jsx("div", { style: style, children: renderInline(heading[2], `h${index}`) }, `h${index++}`));
            continue;
        }
        // 列表
        const ulItem = /^\s*[-*]\s+(.*)$/.exec(line);
        const olItem = /^\s*\d+[.)]\s+(.*)$/.exec(line);
        if (ulItem || olItem) {
            const content = ulItem ? ulItem[1] : olItem[1];
            if (listStack.length === 0 || listStack[listStack.length - 1].ordered !== Boolean(olItem)) {
                flushLists();
                listStack.push({ items: [], ordered: Boolean(olItem) });
            }
            listStack[listStack.length - 1].items.push(_jsx("li", { style: { margin: '2px 0' }, children: renderInline(content, `li${index}`) }, `li-${index++}`));
            continue;
        }
        // 分割线
        if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
            flushLists();
            blocks.push(_jsx("hr", { style: { border: 'none', borderTop: '1px solid rgba(0,0,0,0.12)', margin: '16px 0' } }, `hr-${index++}`));
            continue;
        }
        // 引用
        const quote = /^\s*>\s?(.*)$/.exec(line);
        if (quote) {
            flushLists();
            blocks.push(_jsx("blockquote", { style: { margin: '8px 0', padding: '4px 12px', borderLeft: '3px solid rgba(0,0,0,0.2)', color: 'inherit', opacity: 0.85 }, children: renderInline(quote[1], `q${index}`) }, `q-${index++}`));
            continue;
        }
        // 空行
        if (!line.trim()) {
            flushLists();
            blocks.push(_jsx("div", { style: { height: '8px' } }, `gap-${index++}`));
            continue;
        }
        // 普通段落
        flushLists();
        blocks.push(_jsx("p", { style: { margin: '6px 0', lineHeight: 1.75 }, children: renderInline(line, `p${index}`) }, `p-${index++}`));
    }
    flushLists();
    return blocks;
}
/* -------------------------------------------------------------------------- */
/* 详情页                                                                      */
/* -------------------------------------------------------------------------- */
export function NoteDetailPage(props) {
    const { initialData } = props;
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    const { Link, useParams } = useRoute();
    const { id } = useParams();
    const [note, setNote] = React.useState(initialData ?? null);
    const [loading, setLoading] = React.useState(!initialData);
    const [error, setError] = React.useState(null);
    const didFetch = React.useRef(false);
    React.useEffect(() => {
        const styleId = 'note-detail-page-keyframes';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = KEYFRAMES_CSS;
            document.head.appendChild(style);
        }
    }, []);
    React.useEffect(() => {
        if (!id || didFetch.current)
            return;
        // SSR 已注入数据：标记已获取并跳过网络请求
        if (note) {
            didFetch.current = true;
            return;
        }
        didFetch.current = true;
        setLoading(true);
        setError(null);
        getNoteApiClient()
            .getPublicNote(id)
            .then((result) => setNote(result))
            .catch((err) => setError(err instanceof Error ? err.message : '加载失败'))
            .finally(() => setLoading(false));
    }, [id, note]);
    const colors = note ? getVisibilityColors('public') : { bg: '', text: '' };
    return (_jsxs("div", { style: { minHeight: '100vh', backgroundColor: theme.bg.secondary, fontFamily: typography.fontFamily, color: theme.text.primary }, children: [_jsx("div", { style: { borderBottom: `1px solid ${theme.border.divider}`, padding: isMobile ? '12px 16px' : '16px 24px' }, children: _jsx(Link, { to: "/notes", style: { fontSize: typography.fontSize.base, color: theme.status.primary, textDecoration: 'none', fontWeight: typography.fontWeight.medium }, children: "\u2190 \u8FD4\u56DE\u516C\u5F00\u7B14\u8BB0\u5217\u8868" }) }), _jsxs("main", { style: { maxWidth: '820px', margin: '0 auto', padding: isMobile ? '20px 16px 60px' : '40px 24px 80px' }, children: [loading && (_jsx("div", { style: { padding: '80px 0', textAlign: 'center', color: theme.text.tertiary, fontSize: typography.fontSize.md }, children: _jsx("span", { style: { animation: 'note-pulse 1.5s ease-in-out infinite', display: 'inline-block' }, children: "\u52A0\u8F7D\u4E2D..." }) })), error && (_jsx("div", { style: { padding: spacing.lg, backgroundColor: theme.status.dangerBg, border: `1px solid ${theme.status.danger}40`, borderRadius: radius.md, color: theme.status.danger, fontSize: typography.fontSize.base }, children: error })), !loading && !error && note && (_jsxs("article", { style: { animation: 'note-fade-in-up 0.3s ease forwards' }, children: [_jsx("h1", { style: { margin: '0 0 12px 0', fontSize: isMobile ? typography.fontSize['2xl'] : '32px', fontWeight: typography.fontWeight.bold, lineHeight: 1.3, color: theme.text.primary }, children: note.title || '无标题' }), _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap', marginBottom: spacing.xl, paddingBottom: spacing.lg, borderBottom: `1px solid ${theme.border.divider}` }, children: [_jsxs("span", { style: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: radius.full, backgroundColor: colors.bg, color: colors.text, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium }, children: [_jsx(PublicIcon, {}), "\u516C\u5F00\u5206\u4EAB"] }), _jsxs("span", { style: { fontSize: typography.fontSize.sm, color: theme.text.tertiary }, children: ["\u66F4\u65B0\u4E8E ", new Date(note._updatedAt ?? note._createdAt).toLocaleString()] }), (note.tags ?? []).map(tag => (_jsxs("span", { style: { padding: '2px 10px', borderRadius: radius.full, backgroundColor: theme.interaction.hover, color: theme.text.secondary, fontSize: typography.fontSize.xs }, children: ["#", tag] }, tag)))] }), _jsx("div", { style: { fontSize: typography.fontSize.md, lineHeight: 1.75, color: theme.text.primary }, children: renderMarkdown(note.content ?? '') })] }))] })] }));
}
//# sourceMappingURL=note-detail-page.js.map